import type { BlockId, ComponentsUnionType } from '@unifyapps/defs/types/block';
import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import { useCallback, useMemo } from 'react';
import InterfacePageHelper from '../../../helper/InterfacePage';
import { getBlockAncestors } from '../../../utils/getAncestorBlockTypes';
import { setPanelHistory } from '../../../utils/panelHelpers';
import { getIsSlotBlock, getSlotBlockIds } from '../../../utils/slotBlocks';
import { useInterfaceAppStore, useInterfaceStore } from '../context';
import InterfaceHelper from '../helper/InterfaceHelper';
import { useBlockFromPage } from '../hooks';
import type { BuilderInterfaceStoreSelectors } from './selectors';
import { getActiveInterfacePage } from './state';
import type { InterfaceStoreType } from './types';

export const useBuilderInterfaceStore = () => {
  return useInterfaceStore<BuilderInterfaceStoreSelectors>();
};

export const useBuilderInterfaceAppStore = () => {
  return useInterfaceAppStore<BuilderInterfaceStoreSelectors>();
};

export const useGetBuilderInterfaceStoreState = () => {
  return {
    getBuilderInterfaceStoreState: useBuilderInterfaceStore().getState,
  };
};

export const useBrandAssets = () => {
  const interfaceRecord = useBuilderInterfaceStore().hooks.record.useDetails();
  return InterfaceHelper.getBrandAssets(interfaceRecord);
};

export const useGetBuilderInterfaceAppStoreState = () => {
  return {
    getBuilderInterfaceAppStoreState: useBuilderInterfaceAppStore().getState,
  };
};

export const useActivePropertyPanelHistory = () => {
  const propertyPanelHistory = useBuilderInterfaceStore().hooks.usePropertyPanel().history;
  return {
    activeState: propertyPanelHistory[propertyPanelHistory.length - 1],
    historyLength: propertyPanelHistory.length,
  };
};

export type PanelBreadcrumbItem = {
  label: string;
  description?: string;
  index: number;
  // false when the crumb is synthesized (has no real history index to
  // navigate to). HeaderBreadcrumbs renders it as static text.
  navigable?: boolean;
  // set on synthesized crumbs that navigate by block id rather than a history
  // index (e.g. the active block's tree parent surfaced as a leading crumb).
  blockId?: string;
  // true when the block is a fixed component slot of its parent (Card body,
  // Button body, …). The header marks these so a slot reads differently from a
  // normal block. Page root regions (body/header/footer) are not slots. See
  // getIsSlotBlock.
  isSlot?: boolean;
};

/**
 * reads breadcrumb items directly from the panel history stack.
 * every entry's `breadcrumb` is required at push time (see the
 * PUSH action). For COMPONENT_OVERRIDE_DETAILS entries whose parent
 * TemplateComponent block is not already the predecessor on the
 * stack (e.g. history restored via SET_HISTORY / deep-link), we
 * synthesize a non-navigable parent crumb so the header still shows
 * `{parent-template-name} / {child-slot-name}`.
 */
export const usePanelBreadcrumbs = (): PanelBreadcrumbItem[] => {
  const builderStore = useBuilderInterfaceStore();
  const propertyPanelHistory = builderStore.hooks.usePropertyPanel().history;
  return useMemo(() => {
    const activePage = getActiveInterfacePage(builderStore.getState());
    const resolveDisplayName = (blockId: BlockId) =>
      InterfacePageHelper.getBaseDeviceBlock(activePage, blockId)?.displayName ?? blockId ?? '';

    // slot set is built once per page snapshot; every crumb below is tagged
    // against it so both the trail and the title can mark slots.
    const blocks = InterfacePageHelper.getBaseDeviceBlocks(activePage);
    const slotBlockIds = blocks ? getSlotBlockIds(blocks) : new Set<BlockId>();
    const isSlot = (blockId: BlockId | undefined) => getIsSlotBlock(blockId, slotBlockIds);

    const items: PanelBreadcrumbItem[] = [];
    propertyPanelHistory.forEach((entry, index) => {
      // fallback: resolve live displayName for the initial BLOCK_DETAILS
      // entry (constructed at store init with an empty label) so renames
      // stay in sync without replaying history.
      let label: string | undefined = entry.breadcrumb.label;
      if (!label && entry.viewDetails.viewType === 'BLOCK_DETAILS' && entry.blockId) {
        label = resolveDisplayName(entry.blockId);
      }
      // Surface the block's ancestor chain (top-most → immediate parent) as
      // leading crumbs so the header shows exactly where the block lives and
      // lets the user jump to any level in one click. Only for the panel root
      // (index 0) — deeper entries already carry their predecessor as context.
      // packVisibleCrumbs folds the middle into a "…" popover only when the
      // chain overflows the header width, so depth never breaks the layout.
      // Top-level blocks (header/body/footer roots) have no ancestor, so
      // nothing is added.
      // getBlockAncestors resolves each parent structurally (not via parentId
      // alone), which composite/slot children may not have set.
      if (index === 0 && entry.viewDetails.viewType === 'BLOCK_DETAILS' && entry.blockId) {
        if (blocks) {
          getBlockAncestors(blocks, entry.blockId).forEach((ancestor) => {
            items.push({
              label: ancestor.label,
              index: -1,
              navigable: true,
              blockId: ancestor.blockId ?? undefined,
              isSlot: isSlot(ancestor.blockId),
            });
          });
        }
      }
      if (entry.viewDetails.viewType === 'COMPONENT_OVERRIDE_DETAILS') {
        const parentId = (
          entry.viewDetails.data as { parentTemplateComponentBlockId?: BlockId } | undefined
        )?.parentTemplateComponentBlockId;
        const prevIsParent = propertyPanelHistory[index - 1]?.blockId === parentId;
        if (parentId && !prevIsParent) {
          items.push({ label: resolveDisplayName(parentId), index: -1, navigable: false });
        }
      }
      items.push({
        label,
        description: entry.breadcrumb.description,
        index,
        isSlot: isSlot(entry.blockId),
      });
    });
    return items;
  }, [propertyPanelHistory, builderStore]);
};

/**
 * returns a stable callback to navigate to a specific breadcrumb index.
 * slices the panel history to the given index and atomically sets it.
 *
 * The new active block is the blockId of the TARGET history entry —
 * not the caller's current activeBlockId — so cross-block navigation
 * (e.g. clicking a parent crumb after drilling into a slot block) lands
 * on the right block. The `fallbackBlockId` param is only used if the
 * target entry can't be resolved, which should not happen in practice.
 */
export const useNavigateToBreadcrumb = (fallbackBlockId: BlockId) => {
  const builderStore = useBuilderInterfaceStore();
  const { propertyPanelHistoryActions } = builderStore.hooks.useActions();

  return useCallback(
    (index: number, blockId?: BlockId) => {
      // Synthesized crumbs (e.g. the auto-surfaced tree parent) aren't in the
      // history stack, so there's no index to slice to — navigate straight to
      // the block by resetting the panel to it. Its own parent then surfaces
      // as the next leading crumb, so the user can climb the tree one level
      // per click.
      if (index < 0) {
        if (!blockId) return;
        setPanelHistory(
          propertyPanelHistoryActions,
          [
            {
              blockId,
              viewDetails: { viewType: 'BLOCK_DETAILS', data: undefined },
              breadcrumb: { label: '' },
            },
          ] as InterfaceStoreType['propertyPanel']['history'],
          blockId,
        );
        return;
      }
      const storeState = builderStore.getState();
      const fullHistory = storeState.propertyPanel.history;
      const slicedHistory = fullHistory.slice(0, index + 1);
      const targetBlockId = slicedHistory[slicedHistory.length - 1]?.blockId ?? fallbackBlockId;
      setPanelHistory(propertyPanelHistoryActions, slicedHistory, targetBlockId);
    },
    [builderStore, propertyPanelHistoryActions, fallbackBlockId],
  );
};

export const useIsActiveInterfacePageDirty = () => {
  const activeInterfacePageId = useBuilderInterfaceStore().hooks.useActiveInterfacePageId();
  const dirtyPages = useBuilderInterfaceStore().hooks.useDirtyPages();
  return dirtyPages.has(activeInterfacePageId);
};

export const useIsActiveGlobalInterfacePageDirty = () => {
  const activeGlobalInterfacePageId =
    useBuilderInterfaceAppStore().hooks.useActiveInterfacePageId();
  const dirtyPages = useBuilderInterfaceAppStore().hooks.useDirtyPages();
  return dirtyPages.has(activeGlobalInterfacePageId);
};

export const useActiveBlockFromPage = <T = ComponentsUnionType>() => {
  const blockId = useBuilderInterfaceStore().hooks.useActiveBlockId();
  return useBlockFromPage<T>({ blockId });
};

export const useAppVariableDetails = (variableId: string) => {
  const pageVariable = useInterfaceStore().hooks.page.usePageVariableById(variableId);
  const globalPageVariable =
    useBuilderInterfaceAppStore().hooks.page.usePageVariableById(variableId);
  return pageVariable || globalPageVariable;
};

export const useUnsavedAppVariableDetails = (variableId: string) => {
  const pageEntities = useBuilderInterfaceStore().hooks.useUnsavedJavascriptEntities();
  const globalEntities = useBuilderInterfaceAppStore().hooks.useUnsavedJavascriptEntities();
  const pageVariable = pageEntities[variableId]?.entity as JavaScriptVariable | undefined;
  const globalVariable = globalEntities[variableId]?.entity as JavaScriptVariable | undefined;
  return globalVariable ?? pageVariable;
};

export const useWalkthroughStepTargetPageId = () => {
  const activeStepId = useBuilderInterfaceStore().hooks.walkthrough.useActiveStepId();
  useBuilderInterfaceStore().hooks.walkthrough.useStepById(activeStepId);
  const walkthroughTargetPageId = useBuilderInterfaceStore().hooks.walkthrough.useTargetPageId();

  return walkthroughTargetPageId;
};
